import { useAtomValue } from "jotai";
import Konva from "konva";
import { useRef, useMemo, useCallback } from "react";
import { Line } from "react-konva";
import { useEvent, useKey } from "react-use";
import { stageRefAtom } from "state-atoms";
import { fullCellKey, screenToElementMatrix } from "../table-utils";
import type { TypeTableCell, TypeTableElement } from "shared/datamodel/schemas/table";
import { findClosest } from "../find-closest";
import { clamp } from "utils/math-utils";
import { Cell, WidgetOffsetFromTable } from "../table-styling";
import { DragHandle } from "../widgets/drag-handle";

export function ColumnDragger({
  id,
  col,
  updateDrag,
  xs,
  ys,
  element,
  cells,
}: {
  col: number;
  id: string;
  xs: number[];
  ys: number[];
  element: TypeTableElement;
  cells: Record<string, TypeTableCell>;
  updateDrag: (
    action: { type: "drag"; src: number; dx?: number; dy?: number } | { type: "finish"; src: number; target: number }
  ) => void;
}) {
  const stageRef = useAtomValue(stageRefAtom);
  const stage = stageRef?.current ?? null;
  const ref = useRef<any>();
  const lineref = useRef<any>();
  const { scaleX = 1, scaleY = 1 } = element;

  const nodes = useMemo(() => {
    const colId = element.cols[col].id;
    // get all contained elements in this column
    const childIds = element.rows.flatMap(
      ({ id: rowId }: { id: string }) => cells[fullCellKey(id, colId, rowId)]?.containedIds ?? []
    );
    const layer = stage
      ?.getLayers()
      .toArray()
      .find((x) => x.name() == "Elements") as Konva.Layer;
    const nodes = layer!.find((node: any) => childIds.includes(node.id())).toArray();
    return nodes;
  }, []);

  const finishOperation = useCallback(
    (e) => {
      const matrix = screenToElementMatrix(stage, element);
      const point = matrix.transformPoint({ x: e.clientX, y: e.clientY });
      let target = findClosest(point.x, xs);
      if (target > col) target--; // don't ask....
      nodes.forEach((node: any) => node.offset({ x: 0, y: 0 }));
      updateDrag({ type: "finish", src: col, target });
    },
    [nodes]
  );

  useEvent(
    "mousemove",
    useCallback(
      (e) => {
        /**
         * Sometimes when the user moves the mouse fast and lets go of the button, we get
         * "mousemove" event with buttons==0 before the "mouseup". In this case we end the drag.
         */
        if (e.buttons == 0) {
          finishOperation(e);
          return;
        }
        const matrix = screenToElementMatrix(stage, element);
        const point = matrix.transformPoint({ x: e.clientX, y: e.clientY });
        const target = findClosest(point.x, xs);
        const x = clamp(point.x, 0, xs[xs.length - 1]);
        const { scaleX = 1 } = element;
        ref.current!.x(element.x + x * scaleX);
        if (target == col || target == col + 1) {
          lineref.current.visible(false);
        } else {
          lineref.current.visible(true);
          lineref.current!.x(element.x + xs[target] * scaleX);
        }
        let dx = x - (xs[col] + xs[col + 1]) / 2; // dx in canvas-pixels
        updateDrag({ type: "drag", src: col, dx });
        nodes.forEach((node: any) => node.offset({ x: (-dx * scaleX) / node.scaleX(), y: 0 }));
      },
      [nodes]
    )
  );

  /**
   * On escape cancel this whole shenaningans, and don't apply any changes
   */
  useKey("Escape", () => {
    updateDrag({ type: "finish", src: col, target: col });
    nodes.forEach((node: any) => node.offset({ x: 0, y: 0 }));
  });

  useEvent("mouseup", finishOperation);

  if (!stage) {
    return null; // we should never reach this line - dragging column is impossible unless table is on the stage
  }

  return (
    <>
      <DragHandle
        ref={ref}
        y={element.y}
        x={((xs[col] + xs[col + 1]) * scaleX) / 2}
        ofsY={-WidgetOffsetFromTable}
        mode="horizontal"
      />
      <Line
        ref={lineref}
        visible={false}
        y={element.y}
        x={element.x + ((xs[col] + xs[col + 1]) * scaleX) / 2}
        points={[0, 0, 0, ys[ys.length - 1] * scaleY]}
        strokeWidth={4}
        stroke={Cell.SelectedStrokeColor}
        strokeScaleEnabled={false}
      />
    </>
  );
}
